import { useState, useEffect } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTenant } from '../contexts/TenantContext';
import { AuthLayout } from '../layouts/AuthLayout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { CheckCircle2, XCircle, Loader2, Mail } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const VerifyEmailPage = () => {
    const { orgCode } = useParams();
    const [searchParams] = useSearchParams();
    const { login } = useAuth();
    const { tenant } = useTenant();
    const navigate = useNavigate();
    
    const [status, setStatus] = useState('verifying'); // verifying, success, error
    const [error, setError] = useState('');
    
    const token = searchParams.get('token');

    useEffect(() => {
        if (token) {
            verifyEmail();
        } else {
            setStatus('error');
            setError('Kein Verifizierungstoken gefunden');
        }
    }, [token]);

    const verifyEmail = async () => {
        try {
            const response = await axios.post(`${API}/auth/verify-email`, { token });
            
            // Auto-login after verification
            localStorage.setItem('token', response.data.token);
            
            setStatus('success');
            toast.success('E-Mail erfolgreich verifiziert!');
            
            // Redirect to dashboard after 2 seconds
            setTimeout(() => {
                navigate(`/o/${orgCode}/dashboard`);
                window.location.reload();
            }, 2000);
        } catch (err) {
            setStatus('error');
            setError(err.response?.data?.detail || 'Verifizierung fehlgeschlagen');
        }
    };

    return (
        <AuthLayout>
            <Card className="border-0 shadow-lg text-center">
                <CardContent className="pt-12 pb-8">
                    {status === 'verifying' && (
                        <>
                            <div 
                                className="w-20 h-20 rounded-full mx-auto flex items-center justify-center mb-6"
                                style={{ backgroundColor: `${tenant?.primary_color}15` }}
                            >
                                <Loader2 
                                    size={40} 
                                    className="animate-spin"
                                    style={{ color: tenant?.primary_color }} 
                                />
                            </div>
                            <h2 className="text-2xl font-bold text-slate-900 mb-2">
                                E-Mail wird verifiziert...
                            </h2>
                            <p className="text-slate-600">
                                Bitte warten Sie einen Moment.
                            </p>
                        </>
                    )}
                    
                    {status === 'success' && (
                        <>
                            <div className="w-20 h-20 rounded-full mx-auto flex items-center justify-center mb-6 bg-green-50">
                                <CheckCircle2 size={40} className="text-green-600" />
                            </div>
                            <h2 className="text-2xl font-bold text-slate-900 mb-2">
                                E-Mail verifiziert!
                            </h2>
                            <p className="text-slate-600 mb-6">
                                Ihr Konto wurde erfolgreich aktiviert. Sie werden weitergeleitet...
                            </p>
                            <Button
                                style={{ backgroundColor: tenant?.primary_color }}
                                onClick={() => navigate(`/o/${orgCode}/dashboard`)}
                            >
                                Zum Dashboard
                            </Button>
                        </>
                    )}
                    
                    {status === 'error' && (
                        <>
                            <div className="w-20 h-20 rounded-full mx-auto flex items-center justify-center mb-6 bg-red-50">
                                <XCircle size={40} className="text-red-600" />
                            </div>
                            <h2 className="text-2xl font-bold text-slate-900 mb-2">
                                Verifizierung fehlgeschlagen
                            </h2>
                            <p className="text-slate-600 mb-6">
                                {error}
                            </p>
                            <div className="flex flex-col sm:flex-row gap-3 justify-center">
                                <Link to={`/o/${orgCode}/login`}>
                                    <Button variant="outline">
                                        Zur Anmeldung
                                    </Button>
                                </Link>
                                <Link to={`/o/${orgCode}/register`}>
                                    <Button style={{ backgroundColor: tenant?.primary_color }}>
                                        Erneut registrieren
                                    </Button>
                                </Link>
                            </div>
                        </>
                    )}
                </CardContent>
            </Card>
        </AuthLayout>
    );
};

export const PendingVerificationPage = () => {
    const { orgCode } = useParams();
    const { tenant } = useTenant();
    const [email, setEmail] = useState('');
    const [resending, setResending] = useState(false);

    useEffect(() => {
        const storedEmail = localStorage.getItem('pending_verification_email');
        if (storedEmail) {
            setEmail(storedEmail);
        }
    }, []);

    const handleResend = async () => {
        if (!email) {
            toast.error('Keine E-Mail-Adresse gefunden');
            return;
        }
        
        setResending(true);
        try {
            await axios.post(`${API}/auth/resend-verification`, {
                email,
                organization_code: orgCode
            });
            toast.success('Verifizierungsmail wurde erneut gesendet');
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Fehler beim Senden');
        } finally {
            setResending(false);
        }
    };

    return (
        <AuthLayout>
            <Card className="border-0 shadow-lg text-center">
                <CardContent className="pt-12 pb-8">
                    <div 
                        className="w-20 h-20 rounded-full mx-auto flex items-center justify-center mb-6"
                        style={{ backgroundColor: `${tenant?.primary_color}15` }}
                    >
                        <Mail size={40} style={{ color: tenant?.primary_color }} />
                    </div>
                    <h2 className="text-2xl font-bold text-slate-900 mb-2">
                        Bestätigen Sie Ihre E-Mail
                    </h2>
                    <p className="text-slate-600 mb-2">
                        Wir haben eine Bestätigungsmail an
                    </p>
                    <p className="font-medium text-slate-900 mb-6">
                        {email || 'Ihre E-Mail-Adresse'}
                    </p>
                    <p className="text-slate-500 text-sm mb-8">
                        Bitte klicken Sie auf den Link in der E-Mail, um Ihr Konto zu aktivieren.
                    </p>
                    
                    <div className="space-y-3">
                        <Button
                            variant="outline"
                            onClick={handleResend}
                            disabled={resending}
                            className="w-full"
                        >
                            {resending ? (
                                <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    Wird gesendet...
                                </>
                            ) : (
                                'E-Mail erneut senden'
                            )}
                        </Button>
                        <Link to={`/o/${orgCode}/login`} className="block">
                            <Button variant="ghost" className="w-full">
                                Zurück zur Anmeldung
                            </Button>
                        </Link>
                    </div>
                </CardContent>
            </Card>
        </AuthLayout>
    );
};
